import React from "react";
import cmsData from "../cms.json";

const upcomingShows = cmsData.shows || [];

const UpcomingShows = () => {
  const formatDate = (dateString) => {
    // Manually parse the date string to prevent cross-browser issues,
    // especially on Safari which is strict about the format.
    const parts = dateString.split("-");
    // Note: The month argument in the Date constructor is 0-indexed (0=Jan, 1=Feb, etc).
    const date = new Date(parts[0], parts[1] - 1, parts[2]);

    const day = date.getDate().toString().padStart(2, "0");
    const month = date
      .toLocaleDateString("en-US", { month: "short" })
      .toUpperCase();
    const year = date.getFullYear();
    return { day, month, year };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "sold-out":
        return "#888";
      case "presale":
        return "#fff";
      case "on-sale":
        return "#fff";
      case "venue-only":
        return "#929493";
      case "tba":
        return "#929493";
      default:
        return "#fff";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "sold-out":
        return "SOLD OUT";
      case "presale":
        return "PRESALE";
      case "on-sale":
        return "ON SALE";
      case "venue-only":
        return "AT VENUE";
      case "tba":
        return "TO BE ANNOUNCED";
      default:
        return "TICKETS";
    }
  };

  const isClickableStatus = (status) => {
    return status !== "sold-out" && status !== "venue-only" && status !== "tba";
  };

  return (
    <section className="shows-section" id="shows">
      <div className="shows-container">
        <div className="section-header">
          <h2 className="section-title">SEE US LIVE</h2>
          <div className="section-line"></div>
        </div>

        {upcomingShows.length > 0 ? (
          <div className="shows-list">
            {upcomingShows.map((show, index) => {
              const { day, month, year } = formatDate(show.date);
              return (
                <div
                  key={show.id}
                  className="show-item"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="show-date">
                    <div className="date-day">{day}</div>
                    <div className="date-month">{month}</div>
                    <div className="date-year">{year}</div>
                  </div>

                  <div className="show-details">
                    <div className="show-main-info">
                      <div className="show-venue-row">
                        <h3 className="show-venue">{show.venue}</h3>
                        {show.logo && (
                          <img className="show-logo-icon" src={show.logo} alt="" aria-hidden="true" />
                        )}
                      </div>
                      <p className="show-location">{show.city}</p>
                      {show.status === "venue-only" && show.venueInfo && (
                        <p className="venue-info">{show.venueInfo}</p>
                      )}
                    </div>

                    <div className="show-meta">
                      {show.type === "festival" && (
                        <span className="show-type">FESTIVAL</span>
                      )}
                      {show.type === "support" && (
                        <span className="show-type">SUPPORT</span>
                      )}
                      {show.type === "headline" && (
                        <span className="show-type">HEADLINE SHOW</span>
                      )}
                    </div>
                  </div>

                  <div className="show-action">
                    {show.image && (
                      <div
                        className="show-photo"
                        style={{ backgroundImage: `url(${show.image})` }}
                        aria-hidden="true"
                      />
                    )}

                    {!isClickableStatus(show.status) ? (
                      <div
                        className={`status-badge ${show.status}`}
                        style={{
                          borderColor: getStatusColor(show.status),
                          color: getStatusColor(show.status),
                        }}
                      >
                        {getStatusText(show.status)}
                      </div>
                    ) : (
                      <a
                        href={show.ticketUrl}
                        className="ticket-button"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {getStatusText(show.status)}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-shows-state">
            <div className="no-shows-content">
              <h3 className="no-shows-title">No shows currently scheduled</h3>
              <p className="no-shows-text">
                We're working on bringing you more live experiences. Stay tuned
                for announcements.
              </p>
              <div className="no-shows-actions">
                <a
                  href="https://instagram.com/thecreaksband"
                  className="cta-button primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Follow on Instagram
                </a>
                <a href="#booking" className="cta-button outline">
                  Book The Creaks
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .shows-section {
          background-color: #000;
          color: #fff;
          padding: clamp(60px, 8vw, 120px) 0;
          width: 100%;
        }

        .shows-container {
          padding: 0 clamp(20px, 5vw, 48px);
        }

        .section-header {
          margin-bottom: clamp(40px, 6vw, 80px);
          position: relative;
        }

        .section-title {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 600;
          font-size: clamp(1.6rem, 3.5vw, 2.6rem);
          line-height: 1.2;
          color: #ffffff;
          margin: 0 0 20px 0;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }

        .section-line {
          width: 60px;
          height: 1px;
          background-color: #fff;
          opacity: 0.6;
        }

        /* Shows List Styles */
        .shows-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
          background-color: rgba(255, 255, 255, 0.1);
        }

        .show-item {
          background-color: #000;
          padding: clamp(20px, 3vw, 32px) clamp(16px, 3vw, 28px);
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: clamp(20px, 3vw, 40px);
          transition: border-color 0.2s;
          opacity: 0;
          animation: slideInShow 0.6s ease-out forwards;
          border: none;
          border-left: 2px solid transparent;
          cursor: pointer;
          min-height: clamp(140px, 18vw, 180px);
        }

        .show-photo {
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          aspect-ratio: 16 / 9;
          z-index: -1;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          filter: brightness(1);
          transition: filter 0.2s;
          pointer-events: none;
        }

        .show-photo::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to left,
            rgba(0, 0, 0, 0.35) 0%,
            rgba(0, 0, 0, 0.7) 65%,
            #000 100%
          );
        }

        .show-item:hover {
          border-left-color: rgba(255, 255, 255, 0.3);
        }

        .show-item:hover .show-photo {
          filter: brightness(1.2);
        }

        @keyframes slideInShow {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .show-date {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 56px;
          text-align: center;
          flex-shrink: 0;
        }

        .date-day {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 700;
          font-size: clamp(1.8rem, 3vw, 2.4rem);
          line-height: 1;
          color: #fff;
        }

        .date-month {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 600;
          font-size: 0.7rem;
          letter-spacing: 1px;
          color: #888;
          margin-top: 2px;
        }

        .date-year {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 400;
          font-size: 0.6rem;
          color: #666;
          margin-top: 1px;
        }

        .show-details {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .show-main-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .show-venue-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .show-logo-icon {
          height: clamp(22px, 3.2vw, 32px);
          width: auto;
          flex-shrink: 0;
          filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.7));
        }

        .show-venue {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 600;
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          line-height: 1.2;
          color: #fff;
          margin: 0;
        }

        .show-location {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 400;
          font-size: 0.9rem;
          color: #888;
          margin: 0;
        }

        .venue-info {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 600;
          font-size: 1rem;
          color: rgb(255, 255, 255);
          margin: 4px 0 0 0;
          line-height: 1.3;
        }

        .show-meta {
          display: flex;
          gap: 12px;
        }

        .show-type {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 600;
          font-size: 0.6rem;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #666;
          padding: 2px 8px;
          border: 1px solid #333;
          background-color: rgba(255, 255, 255, 0.02);
        }

        .show-action {
          position: relative;
          z-index: 0;
          flex-shrink: 0;
          align-self: stretch;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        .ticket-button {
          border: 2px solid #fff;
          border-radius: 0;
          padding: 12px 24px;
          text-decoration: none;
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 700;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          transition: all 0.3s;
          cursor: pointer;
          color: white;
          background-color: transparent;
          display: inline-block;
        }

        .ticket-button:hover {
          background-color: #fff;
          color: #000;
        }

        .status-badge {
          padding: 12px 24px;
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 700;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          border: 2px solid #888;
          color: #888;
          background-color: transparent;
          cursor: default;
          display: inline-block;
        }

        .status-badge.sold-out {
          opacity: 0.6;
        }

        .status-badge.venue-only {
          border-color: #929493;
          color: #929493;
          position: relative;
        }

        /* No Shows State */
        .no-shows-state {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 300px;
          padding: clamp(40px, 6vw, 80px) 0;
        }

        .no-shows-content {
          text-align: center;
          max-width: 500px;
          animation: fadeInContent 0.8s ease-out;
        }

        .no-shows-title {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 600;
          font-size: clamp(1.4rem, 3vw, 1.8rem);
          line-height: 1.2;
          color: #fff;
          margin: 0 0 16px 0;
        }

        .no-shows-text {
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 400;
          font-size: clamp(0.95rem, 2vw, 1.1rem);
          line-height: 1.5;
          color: #888;
          margin: 0 0 32px 0;
          max-width: 35ch;
          margin-left: auto;
          margin-right: auto;
        }

        .no-shows-actions {
          display: flex;
          flex-direction: column;
          gap: 16px;
          align-items: center;
        }

        .cta-button {
          border: 2px solid #fff;
          border-radius: 0;
          padding: 14px 32px;
          text-decoration: none;
          font-family: "Archivo Narrow", sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          transition: all 0.3s;
          cursor: pointer;
          color: white;
          background-color: #fff;
          display: inline-block;
        }

        .cta-button:hover {
          background-color: transparent;
          color: #fff;
        }

        .cta-button.outline {
          background-color: transparent;
          color: #fff;
        }

        .cta-button.outline:hover {
          background-color: #fff;
          color: #000;
        }

        .cta-button.primary {
          background-color: #fff;
          color: #000;
        }

        .cta-button.primary:hover {
          background-color: transparent;
          color: #fff;
        }

        @keyframes fadeInContent {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Mobile Responsiveness */
        @media (max-width: 600px) {
          .shows-section {
            padding: clamp(40px, 6vw, 60px) 0;
          }

          .section-header {
            margin-bottom: clamp(30px, 5vw, 50px);
          }

          .show-venue {
            font-size: 1rem;
          }

          .show-item {
            flex-wrap: wrap;
            row-gap: 16px;
          }

          .show-details {
            flex-basis: 60%;
          }

          .show-action {
            flex-basis: 100%;
            justify-content: center;
          }

          .ticket-button,
          .status-badge {
            padding: 10px 16px;
            font-size: 0.7rem;
            width: 100%;
            text-align: center;
          }

          .no-shows-actions {
            flex-direction: column;
            gap: 12px;
            width: 100%;
          }

          .cta-button {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </section>
  );
};

export default UpcomingShows;
